/**
 * Session replay targeting (spec F), pure so the PostHog sync script (WP7) and
 * the browser package share one table.
 *
 * Trigger-group URL conditions depend on the project (Main or Dev): they hold
 * the placeholder `REPLAY_HOSTS_PLACEHOLDER`, replaced by the escaped host
 * alternation `H` in `replayTriggerGroups(env)`.
 */

import { HOSTS, environmentFromHost } from './hosts';

export type ReplayProject = 'main' | 'dev';

/** The SDK's remote trigger-group shape (`version: 2`). */
export interface TriggerGroup {
  readonly id: string;
  readonly name: string;
  readonly sampleRate: number;
  readonly minDurationMs: number;
  readonly conditions: {
    readonly matchType: 'any';
    readonly events: readonly { readonly name: string }[];
    readonly urls: readonly { readonly url: string; readonly matching: 'regex' }[];
    readonly flag: null;
  };
}

export const REPLAY_HOSTS_PLACEHOLDER = '__REPLAY_HOSTS__';

/** V3's future production host: safe in Main now because V2's client never records. */
const MAIN_EXTRA_ORIGINS = ['https://app.tagada.io'] as const;
/** Local V3 dev server. The port is kept: URL conditions match the full URL. */
const DEV_EXTRA_ORIGINS = ['http://localhost:5300'] as const;

/**
 * Origins whose sessions may be recorded. Main: every production crm_v3 host
 * (partner hosts too once V3 serves them) plus `app.tagada.io`. Dev: the
 * `*.tagada.dev` crm_v3 hosts plus local V3. The `*.vercel.app` alias is
 * deliberately out of both.
 */
export function replayHosts(env: ReplayProject): string[] {
  const origins =
    env === 'main'
      ? [
          ...HOSTS.crm_v3.filter((h) => environmentFromHost(h) === 'production').map((h) => `https://${h}`),
          ...MAIN_EXTRA_ORIGINS,
        ]
      : [
          ...HOSTS.crm_v3.filter((h) => h === 'tagada.dev' || h.endsWith('.tagada.dev')).map((h) => `https://${h}`),
          ...DEV_EXTRA_ORIGINS,
        ];
  return Array.from(new Set(origins));
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** `H` of spec F: the escaped host parts of `replayHosts(env)`, port included, joined by `|`. */
export function replayHostPattern(env: ReplayProject): string {
  return replayHosts(env)
    .map((origin) => escapeRegex(origin.replace(/^https?:\/\//, '')))
    .join('|');
}

const H = REPLAY_HOSTS_PLACEHOLDER;

function group(
  id: string,
  name: string,
  sampleRate: number,
  minDurationMs: number,
  events: readonly string[],
  urls: readonly string[] = [],
): TriggerGroup {
  return {
    id,
    name,
    sampleRate,
    minDurationMs,
    conditions: {
      matchType: 'any',
      events: events.map((e) => ({ name: e })),
      urls: urls.map((url) => ({ url, matching: 'regex' as const })),
      flag: null,
    },
  };
}

/** The F table. URL conditions carry `REPLAY_HOSTS_PLACEHOLDER`; use `replayTriggerGroups(env)` to send them. */
export const REPLAY_TRIGGER_GROUPS: readonly TriggerGroup[] = [
  group('errors', 'Errors', 1, 5000, [
    '$exception',
    'agent:turn_fail',
    'studio:frame_load_fail',
    'onboarding:account_create_fail',
    'processors:processor_connect_fail',
  ]),
  group('friction', 'Friction', 0.2, 5000, ['ui:error_toast_show', 'api:request_fail']),
  group('struggle', 'Struggle', 0.5, 10000, ['$rageclick', 'ui:permission_deny_view', 'access:v3_gate_view']),
  group(
    'onboarding',
    'Onboarding',
    0.3,
    10000,
    ['onboarding:wizard_step_view'],
    [`^https?://(${H})/(sign-up|account-setup|onboarding)`],
  ),
  group(
    'setup',
    'Setup',
    0.3,
    15000,
    [],
    [`^https?://(${H})/org/[^/]+/(processors/new|payment-flows/new|stores/new|stores/[^/]+/(canvas|integrations))`],
  ),
  group('agent', 'Agent', 0.2, 15000, ['agent:message_send']),
  group('baseline', 'Baseline', 0.02, 30000, []),
];

/** The trigger groups of one project, URL conditions resolved to that project's hosts. */
export function replayTriggerGroups(env: ReplayProject): TriggerGroup[] {
  const pattern = replayHostPattern(env);
  return REPLAY_TRIGGER_GROUPS.map((g) => ({
    ...g,
    conditions: {
      ...g.conditions,
      urls: g.conditions.urls.map((u) => ({ ...u, url: u.url.split(REPLAY_HOSTS_PLACEHOLDER).join(pattern) })),
    },
  }));
}
